import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalStorage } from 'angular-web-storage';
import { Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

import {
  CustomInbox,
  CustomInboxFilters,
  CustomInboxStatus,
  loadCustomInboxFiltersFromGetParams
} from '../../../../../models/custom-inbox';
import { ListResponse } from '../../../../../models/list-response';
import { CustomInboxService } from '../../../../../services/custom-inbox/custom-inbox.service';
import { addFiltersToGetParams } from '../../../../../utils';
import { AccountService } from '../../../../../services/account/account.service';


@Component({
  selector: 'app-custom-inbox-config',
  templateUrl: './custom-inbox-config.component.html',
  styleUrls: ['./custom-inbox-config.component.css']
})
export class CustomInboxConfigComponent implements OnInit {
  public pageNumber = 1;
  @LocalStorage() public pageSizeCustomInboxConfig;
  public filters: CustomInboxFilters = {
    search: '',
    status: undefined
  };

  public filtersChangedSubject = new Subject<null>();

  public customInboxList: ListResponse<CustomInbox>;
  public filtersApplied = false;

  public loadingCustomInboxes = true;

  public serverError: number = null;

  // Make browser history available to the template.
  public history: History;

  public readonly CustomInboxStatus = CustomInboxStatus;

  private listRequestCount = 0;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private customInboxService: CustomInboxService,
    public accountService: AccountService,
  ) {}

  ngOnInit() {
    if (!this.pageSizeCustomInboxConfig) {
      this.pageSizeCustomInboxConfig = 10;
    }
    // This is needed in order for the pageSize to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.pageSizeCustomInboxConfig = this.pageSizeCustomInboxConfig;

    // Make browser history available to the template.
    this.history = history;

    // Handle changes to the filters by reloading the results, subject to
    // debounce.
    this.filtersChangedSubject.pipe(
      map(() => {
        this.loadingCustomInboxes = true;
        addFiltersToGetParams(
          this.filters,
          this.router,
          this.activatedRoute
        );
      }),
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadCustomInboxes();
    });

    this.filters = loadCustomInboxFiltersFromGetParams(this.activatedRoute);

    this.loadCustomInboxes();
  }

  /**
   * Whether a non-zero number of results have been loaded.
   */
  public resultsPresent(): boolean {
    return (
      this.customInboxList &&
      this.customInboxList.results &&
      (this.customInboxList.results.length > 0)
    );
  }

  /**
   * Update the current page number to the given value and load the page of
   * results.
   *
   * @param newPageNumber - The new page number.
   */
  public setPage(newPageNumber: number) {
    this.pageNumber = newPageNumber;
    this.loadCustomInboxes();
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    if (!this.customInboxList) { return 1; }
    return Math.ceil(this.customInboxList.count / this.pageSizeCustomInboxConfig);
  }

  private loadCustomInboxes() {
    this.loadingCustomInboxes = true;

    // Increment the request count and save a local copy.
    this.listRequestCount++;
    const thisRequestCount = this.listRequestCount;

    this.customInboxService
      .getCustomInboxes(
        this.pageNumber,
        this.pageSizeCustomInboxConfig,
        this.filters
      )
      .then(response => {
        if (thisRequestCount === this.listRequestCount) {
          this.customInboxList = response;

          this.filtersApplied = !!(this.filters.search || this.filters.status);
        }
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => {
        if (thisRequestCount === this.listRequestCount) {
          this.loadingCustomInboxes = false;
        }
      });
  }
}
